import {
  readLocalPreference,
  writeLocalPreference,
} from '../local-preference.js'

/**
 * Small per-browser flags for the /apply wizard, in localStorage.
 *
 * Keyed per video id rather than as one wizard-wide flag: the welcome video
 * and the mid-flow video are watched at different moments, and an applicant
 * who has seen the first must still be shown the second. Adding a third
 * video therefore needs no migration.
 *
 * Storage access goes through `lib/local-preference`, which already owns
 * the guard this needs: Safari private mode and storage-blocking extensions
 * throw on `localStorage`, and a video we fail to remember is a harmless
 * re-watch, whereas an uncaught throw here would take the whole wizard down
 * during render. This module is the key naming and the semantics; it does
 * not re-implement the guard.
 */
const KEY_PREFIX = 'bctci:apply-video-seen:'

export function videoSeenKey(videoId: string): string {
  return `${KEY_PREFIX}${videoId}`
}

export function hasSeenVideo(videoId: string): boolean {
  return readFlag(videoSeenKey(videoId))
}

export function markVideoSeen(videoId: string): void {
  setFlag(videoSeenKey(videoId))
}

/**
 * Whether the welcome phase has been got past.
 *
 * Deliberately NOT the welcome video's seen-state, which is written the
 * moment its player mounts. Tying the phase to that would let a page load
 * consume it: a first-time applicant who reloads while the video is still
 * buffering would never see the welcome screen again, and it is where we
 * first set the time expectation and say the answers are saved. The
 * screen is dismissed by the one action that means "I am ready" — pressing
 * Get started.
 */
const WELCOME_DISMISSED_KEY = 'bctci:apply-welcome-dismissed'

export function hasDismissedWelcome(): boolean {
  return readFlag(WELCOME_DISMISSED_KEY)
}

export function markWelcomeDismissed(): void {
  setFlag(WELCOME_DISMISSED_KEY)
}

/**
 * These flags carry no value — the key's presence IS the flag — so `parse`
 * ignores the stored string and simply reports that something was there.
 * `readLocalPreference` only calls it when the key exists.
 */
function readFlag(key: string): boolean {
  return readLocalPreference(key, () => true, false)
}

function setFlag(key: string): void {
  writeLocalPreference(key, '1')
}
